/**
 * Product Variants Editor (Phase 4 #7).
 *
 * Lets the merchant / admin define a list of typed variants per product.
 * Three types:
 *   - SIZE   → label-driven (e.g. "Half", "Full", "Large")
 *   - COLOR  → label + colour hex picker (swatch palette)
 *   - CUSTOM → free-form key=value attrs
 *
 * Each variant carries its OWN `mrp` and `selling_price` — there's no
 * delta logic; the customer pays exactly what the variant says.
 *
 * Backwards-compat: variants saved without a `type` are loaded as
 * `CUSTOM` so legacy product docs keep working.
 */
import { useState } from "react";
import { Plus, Trash2, Palette, Ruler, Sparkles } from "lucide-react";

const DEFAULT_PALETTE = [
    "#000000", "#FFFFFF", "#EF4444", "#F97316", "#F59E0B",
    "#10B981", "#0EA5E9", "#3B82F6", "#8B5CF6", "#EC4899",
    "#78716C", "#6B7280",
];

const TYPES = [
    { value: "SIZE", label: "Size", icon: Ruler },
    { value: "COLOR", label: "Color", icon: Palette },
    { value: "CUSTOM", label: "Custom", icon: Sparkles },
];

const emptyVariant = (type = "SIZE") => ({
    id: `v_${Math.random().toString(36).slice(2, 9)}`,
    type,
    label: "",
    menu_price: 0,
    selling_price: 0,
    mrp: 0,
    unit_of_measure: "",
    color_hex: type === "COLOR" ? "#000000" : null,
    attrs: {},
    is_default: false,
    sort_order: 0,
    enabled: true,
});

export default function ProductVariantsEditor({ value, onChange, testId = "variants" }) {
    const variants = Array.isArray(value) ? value : [];

    const update = (i, patch) => {
        const next = [...variants];
        next[i] = { ...next[i], ...patch };
        onChange(next);
    };
    const remove = (i) => {
        const next = variants.filter((_, idx) => idx !== i);
        onChange(next);
    };
    const add = (type) => {
        const next = [...variants, { ...emptyVariant(type), sort_order: variants.length }];
        onChange(next);
    };
    const setDefault = (i) => {
        const next = variants.map((v, idx) => ({ ...v, is_default: idx === i }));
        onChange(next);
    };

    return (
        <div data-testid={testId} className="space-y-2">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Variants</p>
                    <p className="text-[11px] text-muted-foreground">Each variant has its own price; customers pick one.</p>
                </div>
                <div className="flex gap-1">
                    {TYPES.map(({ value: t, label, icon: Icon }) => (
                        <button
                            key={t}
                            type="button"
                            data-testid={`${testId}-add-${t.toLowerCase()}`}
                            onClick={() => add(t)}
                            className="bg-card border border-border hover:bg-muted rounded-lg px-2.5 py-1.5 text-[11px] font-semibold inline-flex items-center gap-1"
                        >
                            <Icon size={12} /> {label}
                        </button>
                    ))}
                </div>
            </div>

            {variants.length === 0 && (
                <div className="text-[11px] text-muted-foreground bg-muted/30 border border-dashed border-border rounded-xl py-4 text-center">
                    No variants yet — add Size / Color / Custom above to let customers pick.
                </div>
            )}

            <div className="space-y-2">
                {variants.map((v, i) => (
                    <VariantRow
                        key={v.id || i}
                        v={v}
                        i={i}
                        update={(p) => update(i, p)}
                        remove={() => remove(i)}
                        setDefault={() => setDefault(i)}
                        testId={testId}
                    />
                ))}
            </div>
        </div>
    );
}

function VariantRow({ v, i, update, remove, setDefault, testId }) {
    const type = v.type || "CUSTOM";
    return (
        <div data-testid={`${testId}-row-${i}`} className="bg-card border border-border rounded-xl p-2.5">
            <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{type}</span>
                <input
                    data-testid={`${testId}-label-${i}`}
                    className="inp flex-1 text-sm"
                    placeholder={type === "SIZE" ? "e.g. Full, Half, Large" : type === "COLOR" ? "e.g. Sky Blue" : "Variant label"}
                    value={v.label || ""}
                    onChange={(e) => update({ label: e.target.value })}
                />
                <button
                    type="button"
                    data-testid={`${testId}-default-${i}`}
                    onClick={setDefault}
                    title={v.is_default ? "Default variant" : "Make default"}
                    className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${v.is_default ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
                >
                    {v.is_default ? "Default" : "Set default"}
                </button>
                <button
                    type="button"
                    data-testid={`${testId}-remove-${i}`}
                    onClick={remove}
                    className="p-1.5 rounded-lg text-destructive hover:bg-destructive/10"
                ><Trash2 size={13} /></button>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-2">
                <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Menu price ₹</span>
                    <input
                        data-testid={`${testId}-menuprice-${i}`}
                        type="number"
                        className="inp text-sm"
                        placeholder="What you earn (pre-commission)"
                        value={v.menu_price ?? ""}
                        onChange={(e) => update({ menu_price: Number(e.target.value) || 0 })}
                    />
                    <span className="text-[10px] text-muted-foreground mt-0.5 block">
                        {/* iter101 — same behaviour as product-level menu_price:
                            merchant enters wholesale, customer sees SP + struck MRP,
                            payout = menu_price − commission (flat or %). Backend
                            pricing.py applies the same ladder per variant. */}
                        You get: <strong>₹{Math.max(0, Number(v.menu_price) || 0)}</strong> minus commission
                    </span>
                </label>
                <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">UoM</span>
                    <input
                        data-testid={`${testId}-uom-${i}`}
                        className="inp text-sm"
                        placeholder="500g / 1ltr"
                        value={v.unit_of_measure || ""}
                        onChange={(e) => update({ unit_of_measure: e.target.value })}
                    />
                </label>
            </div>
            {(v.selling_price > 0 || v.mrp > 0) && (Number(v.menu_price) > 0) && (
                <div
                    data-testid={`${testId}-preview-${i}`}
                    className="mt-1.5 text-[10px] text-muted-foreground bg-muted/50 rounded px-2 py-1"
                >
                    Customer sees: <strong className="text-foreground">₹{v.selling_price}</strong>
                    {v.mrp > v.selling_price && (
                        <span className="line-through ml-1">₹{v.mrp}</span>
                    )}
                    <span className="text-[9px] ml-1 opacity-70">(auto from menu price)</span>
                </div>
            )}

            {type === "COLOR" && (
                <div className="mt-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Swatch</p>
                    <div className="flex flex-wrap gap-1.5 items-center">
                        {DEFAULT_PALETTE.map((c) => (
                            <button
                                key={c}
                                type="button"
                                data-testid={`${testId}-color-${i}-${c.replace("#", "")}`}
                                title={c}
                                onClick={() => update({ color_hex: c })}
                                className={`w-6 h-6 rounded-full border-2 transition-transform ${v.color_hex === c ? "border-foreground scale-110" : "border-border"}`}
                                style={{ backgroundColor: c }}
                            />
                        ))}
                        <input
                            data-testid={`${testId}-color-hex-${i}`}
                            type="color"
                            className="w-7 h-7 rounded cursor-pointer border border-border"
                            value={v.color_hex || "#000000"}
                            onChange={(e) => update({ color_hex: e.target.value })}
                        />
                    </div>
                </div>
            )}

            {type === "CUSTOM" && (
                <CustomAttrs
                    attrs={v.attrs || {}}
                    onChange={(a) => update({ attrs: a })}
                    testId={`${testId}-attrs-${i}`}
                />
            )}
        </div>
    );
}

function CustomAttrs({ attrs, onChange, testId }) {
    const [k, setK] = useState("");
    const [val, setVal] = useState("");
    const entries = Object.entries(attrs || {});
    const addPair = () => {
        if (!k.trim()) return;
        onChange({ ...attrs, [k.trim()]: val });
        setK(""); setVal("");
    };
    const removeKey = (key) => {
        const n = { ...attrs };
        delete n[key];
        onChange(n);
    };
    return (
        <div className="mt-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Custom attributes</p>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
                {entries.map(([key, v2]) => (
                    <span key={key} data-testid={`${testId}-pair-${key}`} className="inline-flex items-center gap-1 text-[11px] bg-muted px-2 py-0.5 rounded-full">
                        <strong>{key}:</strong> {String(v2)}
                        <button type="button" onClick={() => removeKey(key)} className="text-destructive hover:opacity-70" data-testid={`${testId}-remove-${key}`}>×</button>
                    </span>
                ))}
            </div>
            <div className="flex gap-1.5">
                <input data-testid={`${testId}-key`} className="inp text-xs flex-1" placeholder="key (e.g. material)" value={k} onChange={(e) => setK(e.target.value)} />
                <input data-testid={`${testId}-value`} className="inp text-xs flex-1" placeholder="value" value={val} onChange={(e) => setVal(e.target.value)} />
                <button type="button" data-testid={`${testId}-add-attr`} onClick={addPair} className="bg-card border border-border rounded-lg px-2"><Plus size={13} /></button>
            </div>
        </div>
    );
}
